import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '../../common/Dialog';
import ModalActions from '../../common/ModalActions';
import { getScoringHistory } from '../../../api/scoringApi';
import { loadMultiRecordHandoff } from '../../../pages/scoring/scoringMultiHandoff';
import { SCORING_HANDOFF_MAX_RECORDS } from '../../../pages/scoring/scoringSaenggibuHandoff';
import { trackEvent } from '../../../utils/analytics';
import './ScoringImportModal.css';

const LIST_LIMIT = 100;

// 학년도는 3월에 시작하고, 3~8월이 1학기다.
export const schoolTermOf = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  const month = date.getMonth() + 1;
  const year = month >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${year}-${month >= 3 && month <= 8 ? 1 : 2}`;
};

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
};

const recordTitle = (record) => String(record?.title || record?.subject || '서술형 평가').trim();

const ScoringImportModal = ({ open, onClose, onImport, isPro = false, now = new Date() }) => {
  const [records, setRecords] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setRecords(null);
    setError('');
    setSelected([]);
    getScoringHistory(LIST_LIMIT, 0, isPro)
      .then((response) => {
        if (!cancelled) setRecords(Array.isArray(response?.records) ? response.records : []);
      })
      .catch(() => {
        if (!cancelled) setError('채점 기록을 불러오지 못했어요. 다시 열어 주세요.');
      });
    return () => { cancelled = true; };
  }, [isPro, open]);

  const currentTerm = schoolTermOf(now);
  const termRecords = useMemo(
    () => (records || []).filter(record => schoolTermOf(record.created_at) === currentTerm),
    [currentTerm, records]
  );
  const visible = showAll || termRecords.length === 0 ? (records || []) : termRecords;

  const toggle = (id) => {
    setSelected(previous => {
      if (previous.includes(id)) return previous.filter(item => item !== id);
      if (previous.length >= SCORING_HANDOFF_MAX_RECORDS) return previous;
      return [...previous, id];
    });
  };

  const handleImport = async () => {
    if (selected.length === 0) return;
    setIsImporting(true);
    setError('');
    try {
      const handoff = await loadMultiRecordHandoff(selected);
      if (!handoff) {
        setError('고른 기록에 생기부로 넘길 채점 내용이 없어요.');
        return;
      }
      trackEvent('scoring_import_in_batch', {
        records: handoff.summary?.records || 0,
        students: handoff.summary?.students || 0,
      });
      onImport(handoff.batchRestore);
    } catch (importError) {
      setError(importError?.message || '채점 기록을 불러오지 못했어요. 다시 시도해 주세요.');
    } finally {
      setIsImporting(false);
    }
  };

  const description = records && termRecords.length > 0 && !showAll
    ? '이번 학기 채점 기록이에요. 여러 개를 고르면 학생별로 모아서 써요.'
    : '여러 개를 고르면 학생별로 모아서 써요.';

  return (
    <Dialog
      open={open}
      onClose={isImporting ? undefined : onClose}
      dismissible={!isImporting}
      title="채점 결과 불러오기"
      description={description}
      size="md"
      footer={(
        <ModalActions
          primary={{
            label: selected.length > 0 ? `${selected.length}개 불러오기` : '불러오기',
            onClick: handleImport,
            disabled: selected.length === 0,
            loading: isImporting,
            testId: 'scoring-import-submit',
          }}
          secondary={{ label: '닫기', onClick: onClose, disabled: isImporting }}
        />
      )}
    >
      <div className="scoring-import" data-testid="scoring-import-modal">
        {records === null && !error && (
          <ul className="scoring-import-list" aria-busy="true">
            {[0, 1, 2].map(key => <li key={key} className="scoring-import-skeleton" />)}
          </ul>
        )}
        {records && records.length === 0 && (
          <p className="scoring-import-empty">아직 채점한 기록이 없어요. 서술형 채점을 하면 여기서 바로 불러올 수 있어요.</p>
        )}
        {records && visible.length > 0 && (
          <ul className="scoring-import-list">
            {visible.map((record) => {
              const checked = selected.includes(record.id);
              const disabled = !checked && selected.length >= SCORING_HANDOFF_MAX_RECORDS;
              return (
                <li key={record.id}>
                  <label className={`scoring-import-item ${checked ? 'is-checked' : ''} ${disabled ? 'is-disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(record.id)}
                    />
                    <span className="scoring-import-title">{recordTitle(record)}</span>
                    <span className="scoring-import-meta">
                      {[formatDate(record.created_at), record.student_count ? `${record.student_count}명` : ''].filter(Boolean).join(' · ')}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {records && termRecords.length > 0 && termRecords.length < records.length && (
          <button type="button" className="scoring-import-more" onClick={() => setShowAll(value => !value)}>
            {showAll ? '이번 학기만 보기' : '지난 기록도 보기'}
          </button>
        )}
        {error && <p className="scoring-import-error" role="alert">{error}</p>}
      </div>
    </Dialog>
  );
};

export default ScoringImportModal;
